import React, { useState, useEffect } from "react";
import {
  Modal,
  Result,
  Button,
  Space,
  Typography,
  Spin,
  message,
  QRCode,
  Grid,
} from "antd";
import { payment } from "../../../utils/payment";

const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface WechatPayModalProps {
  visible: boolean;
  onCancel: () => void;
  orderData: {
    order_id: string;
    code_url: string;
    amount: number;
  } | null;
  token: string | null;
}

const WechatPayModal: React.FC<WechatPayModalProps> = ({
  visible,
  onCancel,
  orderData,
  token,
}) => {
  const [checking, setChecking] = useState(false);
  const [paid, setPaid] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // 定时检查支付状态
  useEffect(() => {
    if (!visible || !orderData || paid) return;

    const checkInterval = setInterval(async () => {
      if (orderData.order_id) {
        try {
          // 自动轮询时不显示错误提示
          const isPaid = await payment.queryWechatOrder(
            orderData.order_id,
            token,
            false
          );
          if (isPaid) {
            setPaid(true);
            message.success("支付成功！");
            clearInterval(checkInterval);
            setTimeout(() => {
              window.location.reload();
            }, 1500);
          }
        } catch (error) {
          // 自动轮询时只在控制台输出错误，不显示用户提示
          console.error("自动检查支付状态失败:", error);
        }
      }
    }, 3000); // 每3秒检查一次

    return () => clearInterval(checkInterval);
  }, [visible, orderData, token, paid]);

  // 手动检查支付状态
  const handleCheckPayment = async () => {
    if (!orderData?.order_id) return;

    setChecking(true);
    try {
      // 手动点击时显示错误提示
      const isPaid = await payment.queryWechatOrder(
        orderData.order_id,
        token,
        true
      );
      if (isPaid) {
        setPaid(true);
        message.success("支付成功！");
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        message.info("订单暂未支付");
      }
    } catch (error) {
      console.error("检查支付状态失败:", error);
    } finally {
      setChecking(false);
    }
  };

  // 重置状态
  const handleCancel = () => {
    setPaid(false);
    onCancel();
  };

  if (paid) {
    return (
      <Modal
        open={visible}
        onCancel={handleCancel}
        footer={null}
        width={isMobile ? "90%" : 480}
        centered
        destroyOnClose
      >
        <Result
          status="success"
          title="支付成功"
          subTitle="您的订单已支付成功，页面即将刷新"
          extra={[
            <Button type="primary" key="ok" onClick={handleCancel}>
              确定
            </Button>,
          ]}
        />
      </Modal>
    );
  }

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={isMobile ? "90%" : 480}
      centered
      destroyOnClose
      title={
        <Space>
          <i className="ri-wechat-pay-line" style={{ color: "#09bb07" }} />
          微信支付
        </Space>
      }
    >
      {orderData ? (
        <div
          style={{
            textAlign: "center",
            padding: isMobile ? "16px 0" : "24px 0",
          }}
        >
          <div style={{ marginBottom: 24 }}>
            <Text strong style={{ fontSize: isMobile ? 16 : 18 }}>
              订单金额：
            </Text>
            <Text
              style={{
                fontSize: isMobile ? 20 : 24,
                color: "#ff4d4f",
                fontWeight: "bold",
                marginLeft: 8,
              }}
            >
              ¥{orderData.amount.toFixed(2)}
            </Text>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 24,
              padding: 16,
              background: "#f9f9f9",
              borderRadius: 8,
            }}
          >
            <QRCode
              value={orderData.code_url}
              size={isMobile ? 200 : 240}
              errorLevel="M"
              status="active"
            />
          </div>

          <Paragraph
            style={{
              color: "#666",
              fontSize: isMobile ? 12 : 14,
              marginBottom: 16,
            }}
          >
            请使用微信扫描上方二维码完成支付
          </Paragraph>

          <Paragraph
            style={{
              color: "#999",
              fontSize: 12,
              marginBottom: 24,
            }}
          >
            订单号：{orderData.order_id}
          </Paragraph>

          <Space size="middle">
            <Button
              onClick={handleCheckPayment}
              loading={checking}
              icon={<i className="ri-refresh-line" />}
            >
              检查支付状态
            </Button>
            <Button onClick={handleCancel}>取消</Button>
          </Space>

          <div
            style={{
              marginTop: 16,
              padding: "8px 12px",
              background: "#f0f8ff",
              borderRadius: 4,
              fontSize: 12,
              color: "#666",
            }}
          >
            <i className="ri-information-line" style={{ marginRight: 4 }} />
            支付完成后系统会自动检测，也可点击"检查支付状态"手动验证
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>正在创建订单...</Text>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default WechatPayModal;
